import { ProLightClock } from '@nexelem/icons/ProLightClock';
import { StaticModal, createModal } from '@nexelem/modals';
import { Button } from '@nexelem/components';
import { useDateFormatter } from '@nexelem/utils';
import { FormProvider, useForm } from 'react-hook-form';
import { Trans } from 'react-i18next';
import { useCustomTranslation } from '../../../../../hooks';
import { FormCalendarInput, FormTextInput } from '@nexelem/form-components';
import { z } from 'zod';
import { set } from 'date-fns';
import { LotInfo } from './components/LotInfo';
import { selectOnlyTaskById, useKanbanEventStore } from '../store';
import { getExternalId } from '../utils';
import { useKanbanPendingToInProgressMutation } from './hooks/useKanbanPendingToInProgressMutation';

const schema = z.object({
  startDay: z.date(),
  startHour: z.string(),
  // workerId: z.number().positive(),
});

export type PlannedProductionModalSchema = z.infer<typeof schema>;

type KanbanPendingToInProgressModalProps = {
  id: number;
};

const KanbanPendingToInProgressModal = ({ id }: KanbanPendingToInProgressModalProps) => {
  const { mutate, isPending } = useKanbanPendingToInProgressMutation();
  const formatHours = useDateFormatter({ onlyHours: true });

  const { tWrapper } = useCustomTranslation('products.productionPlan.kanban.modals.plannedProduction');
  const { tWrapper: inputTWrapper } = useCustomTranslation('products.productionPlan.kanban.modals.inputLabels');

  const form = useForm<PlannedProductionModalSchema>({
    // TODO: uncomment when BE will be ready
    // resolver: zodResolver(schema),
    defaultValues: {
      startDay: new Date(),
      startHour: formatHours(new Date()),
      // workerId: 0,
    },
  });

  const task = useKanbanEventStore(selectOnlyTaskById(id));
  const externalId = getExternalId(task);

  if (!externalId) {
    return null;
  }

  const onSubmit = (values: PlannedProductionModalSchema) => {
    const [hours, minutes] = values.startHour.split(':');

    const startDateTime = set(values.startDay, { hours: Number(hours), minutes: Number(minutes) });

    mutate(
      { startDateTime: startDateTime.toISOString(), productionLotId: externalId },
      {
        onSettled: closeKanbanPendingToInProgressModal,
      },
    );
  };
  return (
    <StaticModal component="form" onClose={closeKanbanPendingToInProgressModal} onSubmit={form.handleSubmit(onSubmit)}>
      <StaticModal.Header withCloseButton>{tWrapper('header')}</StaticModal.Header>
      <StaticModal.Content>
        <FormProvider {...form}>
          <div className="flex flex-col gap-y-6">
            <LotInfo id={id} />
            <span className="text-s18">
              <Trans components={{ strong: <strong /> }}>{tWrapper('description')}</Trans>
            </span>
            <div className="flex gap-6">
              <FormCalendarInput required className="max-w-39" label={inputTWrapper('start')} name="startDay" />
              <FormTextInput
                className="pt-6"
                maxWidth={100}
                name="startHour"
                placeholder="08:00"
                StartIcon={ProLightClock}
                type="time"
              />
            </div>
            {/* <SelectFormInput
              required
              label={inputTWrapper('worker.label')}
              name="workerId"
              options={[]}
              placeholder={inputTWrapper('worker.placeholder')}
              StartIcon={ProLightUser}
            /> */}
          </div>
        </FormProvider>
      </StaticModal.Content>
      <StaticModal.Footer>
        <Button variant="outlined" onClick={closeKanbanPendingToInProgressModal}>
          {tWrapper('cancel')}
        </Button>
        <Button isLoading={isPending} type="submit" variant="contained">
          {tWrapper('confirm')}
        </Button>
      </StaticModal.Footer>
    </StaticModal>
  );
};

export const { openKanbanPendingToInProgressModal, closeKanbanPendingToInProgressModal } = createModal(
  KanbanPendingToInProgressModal,
  {
    name: 'KanbanPendingToInProgressModal',
  },
);
